import { FC, useRef, useState, useCallback, useEffect, Fragment } from 'react'
import { useSelector } from 'react-redux'
import { store, Actions } from '@store'
import { Chat } from '@apis'
import { useSocket } from '@providers/SocketProvider'
import { StyleWrapper, StyleColumn } from './style'
import MessageItem from './item'
import MessageItemLoader from './item/loader'
import Empty from '@commons/empty'
import useInfinityScroll from '@hooks/useInfinityScroll'
import download from '@utils/download'
import downloadBase64File from '@utils/downloadBase64File'
interface MessageListProps {
  receiver: any
  sender: any
}
const MessageList: FC<MessageListProps> = ({ receiver, sender }) => {
  const { socket }: any = useSocket()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    receiverId: receiver?.id,
  })
  const {
    items: messages,
    totalPages,
    loading,
  } = useSelector((state: any) => state.messages)
  const room = useSelector((state: any) => state.room)

  const handleClick = async (event: any) => {
    const target = event.target as HTMLElement
    const cross = target.closest('[data-cross-type]')
    if (!!cross) {
      const type = cross.getAttribute('data-cross-type')
      const file = cross.getAttribute('data-cross-file')
      const item: any = {
        type: type,
        file: file,
      }

      if (item?.type == 'image') {
        store.dispatch(
          Actions.set('modal', {
            visible: true,
            file: JSON.parse(item?.file),
            type: 'preview',
          }),
        )
      } else {
        if (!!item?.file?.base64) {
          await downloadBase64File(
            item?.file?.base64,
            item?.file?.fileName,
            item?.file?.extension,
          )
        } else {
          await download(JSON.parse(item?.file))
        }
      }
    }
  }
  const fetchMessages = async (action: any = 'set', params) => {
    await Chat.getMessages({
      params,
      action: action,
    }).catch(() => {})
  }

  const loadMore = async () => {
    const updateParams = {
      ...params,
      page: params?.page + 1,
    }
    if (updateParams.page <= totalPages) {
      await fetchMessages('append', updateParams)
      setParams(updateParams)
    }
  }

  const { observeTarget }: any = useInfinityScroll({
    loading,
    hasMore: params?.page < totalPages ? true : false,
    onLoadMore: loadMore,
    rootRef: scrollContainerRef,
  })

  const onReceiveMessage = useCallback(async (data) => {
    store.dispatch(Actions.prepend('messages', data))
  }, [])

  const onJoinRoom = useCallback((data) => {
    store.dispatch(Actions.set('room', data))
  }, [])

  useEffect(() => {
    if (room?.id === undefined || room?.id === null || !socket) return

    fetchMessages('set', {
      ...params,
      page: 1,
      limit: 10,
      receiverId: receiver?.id,
    })

    socket.on(room?.key, onReceiveMessage)
    return () => {
      socket.off(room?.key, onReceiveMessage)
    }
  }, [room?.id, socket?.id])

  useEffect(() => {
    if (!socket || receiver?.id === undefined || receiver?.id === null) return
    if (typeof room?.id !== undefined) {
      socket.emit('joinRoom', {
        receiverId: `${receiver?.id}`,
      })
    }
    socket.on('joinRoom', onJoinRoom)
    return () => {
      socket.off('joinRoom', onJoinRoom)
      store.dispatch(Actions.reset('room'))
    }
  }, [socket?.id, receiver?.id])

  return (
    <StyleWrapper>
      <StyleColumn ref={scrollContainerRef} onClick={handleClick}>
        {!!loading && !!room?.id ? (
          <>
            {Array(20)
              ?.fill(0)
              ?.map((_: any, index: number) => {
                return <MessageItemLoader index={index} key={index} />
              })}
          </>
        ) : (
          <>
            {!!messages?.length ? (
              <>
                {messages?.map((message: any, index: number) => {
                  const shouldObserve = index === messages.length - 1
                  return (
                    <Fragment key={`${message?.id}-${index}`}>
                      <div ref={shouldObserve ? observeTarget : null}>
                        <MessageItem
                          message={message}
                          sender={sender}
                          receiver={receiver}
                        />
                      </div>
                    </Fragment>
                  )
                })}
              </>
            ) : (
              <Empty />
            )}
          </>
        )}
      </StyleColumn>
    </StyleWrapper>
  )
}
export default MessageList
